export type MotionPermissionState =
  | "unsupported"
  | "needs_permission"
  | "granted"
  | "denied"
  | "fallback_touch";

export interface GyroFrame {
  pitch: number;
  roll: number;
  shake: number;
}

type GyroCallback = (frame: GyroFrame) => void;

interface PermissionCapableOrientationEvent {
  requestPermission?: () => Promise<"granted" | "denied">;
}

export class GyroInputManager {
  private callback: GyroCallback | null = null;
  private baseline: GyroFrame = { pitch: 0, roll: 0, shake: 0 };
  private smoothed: GyroFrame = { pitch: 0, roll: 0, shake: 0 };
  private sensitivity = 0.5;
  private previousAcceleration = 0;
  private listening = false;
  private readonly smoothing = 0.18;

  getStatus(): MotionPermissionState {
    if (typeof window === "undefined" || !("DeviceOrientationEvent" in window)) {
      return "unsupported";
    }

    const eventConstructor = window.DeviceOrientationEvent as unknown as PermissionCapableOrientationEvent;

    return typeof eventConstructor.requestPermission === "function" ? "needs_permission" : "granted";
  }

  setSensitivity(value: number): void {
    this.sensitivity = Math.min(1, Math.max(0, value));
  }

  calibrate(): void {
    this.baseline = { ...this.smoothed, shake: 0 };
  }

  async enable(callback: GyroCallback): Promise<MotionPermissionState> {
    const status = this.getStatus();
    this.callback = callback;

    if (status === "unsupported") {
      return "fallback_touch";
    }

    if (status === "needs_permission") {
      const eventConstructor = window.DeviceOrientationEvent as unknown as PermissionCapableOrientationEvent;
      const permission = await eventConstructor.requestPermission?.();

      if (permission !== "granted") {
        return "denied";
      }
    }

    this.start();

    return "granted";
  }

  disable(): void {
    if (typeof window === "undefined") {
      return;
    }

    window.removeEventListener("deviceorientation", this.handleOrientation);
    window.removeEventListener("devicemotion", this.handleMotion);
    this.listening = false;
  }

  private start(): void {
    if (this.listening || typeof window === "undefined") {
      return;
    }

    window.addEventListener("deviceorientation", this.handleOrientation);
    window.addEventListener("devicemotion", this.handleMotion);
    this.listening = true;
  }

  private readonly handleOrientation = (event: DeviceOrientationEvent): void => {
    const rawPitch = ((event.beta ?? 0) - this.baseline.pitch) / 45;
    const rawRoll = ((event.gamma ?? 0) - this.baseline.roll) / 45;
    const next = {
      pitch: this.clamp(rawPitch * this.sensitivity),
      roll: this.clamp(rawRoll * this.sensitivity),
      shake: this.smoothed.shake
    };

    this.smoothed = {
      pitch: this.mix(this.smoothed.pitch, next.pitch),
      roll: this.mix(this.smoothed.roll, next.roll),
      shake: this.smoothed.shake
    };
    this.callback?.(this.smoothed);
  };

  private readonly handleMotion = (event: DeviceMotionEvent): void => {
    const acceleration = event.accelerationIncludingGravity;
    const magnitude = Math.sqrt(
      (acceleration?.x ?? 0) ** 2 + (acceleration?.y ?? 0) ** 2 + (acceleration?.z ?? 0) ** 2
    );
    const delta = Math.abs(magnitude - this.previousAcceleration);

    this.previousAcceleration = magnitude;
    this.smoothed = {
      ...this.smoothed,
      shake: this.mix(this.smoothed.shake, this.clamp(delta / 14))
    };
    this.callback?.(this.smoothed);
  };

  private mix(current: number, next: number): number {
    return current + (next - current) * this.smoothing;
  }

  private clamp(value: number): number {
    return Math.min(1, Math.max(-1, value));
  }
}
